import { Dispatch } from "redux";
import { message } from "antd";
import { getCountriesWithRegions } from "app/shared/api/getCountriesWithRegions";
import {
  getRegions,
  createRegion,
  saveCountryRegions as saveCountryRegionsApi,
  getDivisionInheritance,
  setDivisionInheritance,
  RegionDto,
} from "app/shared/api/getRegions";
import { getActiveDivisions } from "app/shared/api/getDivision";
import { getSelectedDivisionId } from "app/shared/division/selectedDivisionStorage";
import * as AT from "./actionTypes";

export type DivisionOption = { id: number; name: string };

export const loadCountries = (divisionId: number) => async (dispatch: Dispatch) => {
  dispatch({ type: AT.REGION_MANAGER_LOAD_COUNTRIES_REQUEST });
  try {
    const list = await getCountriesWithRegions(divisionId);
    dispatch({ type: AT.REGION_MANAGER_LOAD_COUNTRIES_SUCCESS, payload: list });
  } catch (e: any) {
    dispatch({
      type: AT.REGION_MANAGER_LOAD_COUNTRIES_FAILURE,
      payload: { error: e?.message || "Failed to load countries" },
    });
  }
};

export const loadRegions = (divisionId: number) => async (dispatch: Dispatch) => {
  dispatch({ type: AT.REGION_MANAGER_LOAD_REGIONS_REQUEST });
  try {
    const list = await getRegions(divisionId);
    dispatch({ type: AT.REGION_MANAGER_LOAD_REGIONS_SUCCESS, payload: list });
  } catch (e: any) {
    dispatch({
      type: AT.REGION_MANAGER_LOAD_REGIONS_FAILURE,
      payload: { error: e?.message || "Failed to load regions" },
    });
  }
};

export const loadDivisions =
  () =>
  async (_dispatch: Dispatch): Promise<DivisionOption[]> => {
    const list = await getActiveDivisions();
    return (list || []).map((d: any) => ({ id: d.id, name: d.name ?? "" }));
  };

export const setInherit = (value: boolean) => ({ type: AT.REGION_MANAGER_SET_INHERIT, payload: value });
export const setSourceDivisionId = (divisionId: number | null) => ({
  type: AT.REGION_MANAGER_SET_SOURCE_DIVISION,
  payload: divisionId,
});

export const loadInheritance = (divisionId: number) => async (dispatch: Dispatch) => {
  try {
    const data = await getDivisionInheritance(divisionId);
    dispatch({
      type: AT.REGION_MANAGER_INHERITANCE_LOADED,
      payload: { inheritedFromDivisionId: data.inheritedFromDivisionId },
    });
  } catch {
    dispatch({
      type: AT.REGION_MANAGER_INHERITANCE_LOADED,
      payload: { inheritedFromDivisionId: null },
    });
  }
};

export const saveInheritance =
  (divisionId: number, inheritedFromDivisionId: number | null) => async (dispatch: Dispatch) => {
    dispatch({ type: AT.REGION_MANAGER_SAVE_REQUEST });
    try {
      await setDivisionInheritance(divisionId, inheritedFromDivisionId);
      dispatch({
        type: AT.REGION_MANAGER_INHERITANCE_SAVED,
        payload: { inheritedFromDivisionId },
      });
      message.success("Region inheritance saved successfully");
    } catch (e: any) {
      dispatch({
        type: AT.REGION_MANAGER_SAVE_FAILURE,
        payload: { error: e?.message || "Failed to save inheritance" },
      });
    }
  };

export const setCountryRegion = (countryId: string | number, regionId: number | null) => ({
  type: AT.REGION_MANAGER_SET_COUNTRY_REGION,
  payload: { countryId, regionId },
});

export const setBulkRegion = (countryIds: (string | number)[], regionId: number | null) => ({
  type: AT.REGION_MANAGER_SET_BULK_REGION,
  payload: { countryIds, regionId },
});

export const selectCountries = (countryIds: (string | number)[]) => ({
  type: AT.REGION_MANAGER_SELECT_COUNTRIES,
  payload: countryIds,
});

export const selectAll = (countryIds: (string | number)[]) => ({
  type: AT.REGION_MANAGER_SELECT_ALL,
  payload: countryIds,
});

export const unselectAll = () => ({ type: AT.REGION_MANAGER_UNSELECT_ALL });

export const setSearch = (value: string) => ({ type: AT.REGION_MANAGER_SET_SEARCH, payload: value });

export type SortConfig = { sortBy: "country" | "region"; sortOrder: "asc" | "desc" };
export const setSort = (sortBy: "country" | "region", sortOrder: "asc" | "desc") => ({
  type: AT.REGION_MANAGER_SET_SORT,
  payload: { sortBy, sortOrder },
});

export const discardChanges = () => ({ type: AT.REGION_MANAGER_DISCARD_CHANGES });

export const openViewRegions = () => ({ type: AT.REGION_MANAGER_OPEN_VIEW_REGIONS });
export const closeViewRegions = () => ({ type: AT.REGION_MANAGER_CLOSE_VIEW_REGIONS });
export const regionsSaved = (regions: RegionDto[]) => ({
  type: AT.REGION_MANAGER_REGIONS_SAVED,
  payload: regions,
});

export const saveCountryRegions =
  (dirtyRows: Array<{ countryId: string | number; regionId: number }>) => async (dispatch: Dispatch) => {
    dispatch({ type: AT.REGION_MANAGER_SAVE_REQUEST });
    const divisionId = getSelectedDivisionId();
    if (!divisionId) {
      dispatch({
        type: AT.REGION_MANAGER_SAVE_FAILURE,
        payload: { error: "No division selected" },
      });
      return;
    }
    try {
      const countryRegions = dirtyRows.map((r) => ({
        countryId: Number(r.countryId),
        regionId: r.regionId,
      }));
      const data = await saveCountryRegionsApi(divisionId, countryRegions);
      message.success("Country regions saved successfully");
      dispatch({
        type: AT.REGION_MANAGER_SAVE_SUCCESS,
        payload: { unassignedCountires: data.unassignedCountires ?? [] },
      });
      await (loadCountries(divisionId) as any)(dispatch);
    } catch (e: any) {
      dispatch({
        type: AT.REGION_MANAGER_SAVE_FAILURE,
        payload: { error: e?.message || "Failed to save" },
      });
    }
  };